<template>
  <h3 class="d-flex align-center">
    <v-icon
      size="x-small"
      v-if="isUrgent"
      icon="mdi-flag"
      class="mr-3 text--color-error-60"></v-icon>
    {{ taskTypeLabel }}
  </h3>

  <check-task-details-title :task="props.task" />

  <v-card variant="flat" class="mt-2">
    <v-card-text class="text-center">
      <task-meta :task="task" class="justify-center"></task-meta>
    </v-card-text>
  </v-card>

  <div class="d-flex mt-2 position-relative gap-2 flex-column flex-md-row double-card">
    <v-card variant="flat" class="flex-fill">
      <v-card-text>
        <h4 class="mb-3">Task Description</h4>
        <span class="font-size-4">{{ taskDescription }}</span>
        <task-checklist
          :is-loading="isLoading"
          :is-disabled="isLoading || props.task.status === YardTaskStatus.CREATED"
          v-if="taskChecklist && taskChecklist.length > 0"
          :checklist-items="taskChecklist"
          :task-id="props.task.id || ''"
          @update-checklist="updateChecklist"
          class="mt-4" />
      </v-card-text>
    </v-card>

    <v-card variant="flat" class="flex-fill">
      <v-card-text>
        <h4 class="mb-3">Attachments</h4>
        <span class="font-size-4">Attachments Placeholder</span>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { computed, unref, toRef } from 'vue';
import type { CheckTaskViewData } from '@nova/core';
import TaskMeta from '@/components/TaskMeta.vue';
import CheckTaskDetailsTitle from '@/components/CheckTaskDetailsTitle.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import TaskChecklist from '@/components/TaskChecklist.vue';
// For some reason, the linter is showing TaskChecklist as an unused import, but it's in the template
import { useYardTask, useTaskQueries } from '@/composables';
import { type YardTaskChecklist, YardTaskStatus } from '@nova/core';

const props = defineProps<{
  task: CheckTaskViewData;
}>();

const task = toRef(props, 'task');

const { taskDescription, getTaskChecklist, getTaskTypeLabel, isUrgent } = useYardTask({
  initialTask: task
});

const taskTypeLabel = computed(() => getTaskTypeLabel());
const taskChecklist = computed(() => getTaskChecklist());
const { useUpdateTaskMutation } = useTaskQueries();

const updateTaskMutation = useUpdateTaskMutation();

const isLoading = computed(() => !!unref(updateTaskMutation.isPending));

const updateChecklist = async (args: { taskId: string; checklist: YardTaskChecklist[] }) => {
  const { checklist } = args;
  if (!props.task.relatedEntity) {
    return;
  }

  const newChild = {
    checklist,
    category: props.task.category || null,
    relatedEntity: props.task.relatedEntity,
    relatedId: props.task.relatedId || null
  };

  const taskId = props.task.id;
  if (!taskId) return;

  await updateTaskMutation.mutateAsync({
    taskId,
    params: {
      child: newChild
    }
  });
};
</script>

<style lang="scss" scoped>
@use 'vuetify/settings' as v;
@use 'sass:map';

.v-card {
  border: 1px solid $color-line-border;
}

.double-card {
  .v-card {
    width: 50%;

    @media #{map.get(v.$display-breakpoints, 'sm-and-down')} {
      width: 100%;
    }
  }
}
</style>
